import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Droplet, Sparkle, X } from "lucide-react";
import FlipCard from "../components/FlipCard";
import { useMessages } from "../context/MessagesContext";
import { getSuerosData } from "../lib/suerosData";

function formatSuggested(text?: string) {
  if (!text) return null;

  if (text.startsWith("Recomendado")) {
    return (
      <>
        <span className="font-bold">Recomendado</span>
        {text.replace("Recomendado", "")}
      </>
    );
  }

  if (text.startsWith("Suggested")) {
    return (
      <>
        <span className="font-bold">Suggested</span>
        {text.replace("Suggested", "")}
      </>
    );
  }

  return text;
}

export default function Sueros() {
  const labels = useMessages();
  const cardItems = getSuerosData(labels);
  const [selectedImage, setSelectedImage] = useState<{
    src: string;
    alt: string;
  } | null>(null);

  useEffect(() => {
    if (!selectedImage) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelectedImage(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage]);

  return (
    <>
      <section id="sueros" className="scroll-mt-24">
        <div className="p-4">
          <h2 className="section-title">
            {labels["drip.title"]}
          </h2>

          <div className="flex flex-wrap justify-center gap-8">
            {cardItems.map((item) => (
              <FlipCard
                key={item.slug}
                className="flex-1 min-w-[280px] max-w-[400px] h-[850px] sm:h-[850px] md:h-[850px] md:aspect-[2/3] lg:aspect-[5/12] xl:aspect-[6/11]"
                frontContent={
                  <div className="flex flex-col gap-4 h-full p-4">
                    <div className="overflow-hidden rounded-2xl h-40 mt-6 flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedImage({ src: item.image, alt: item.title })
                        }
                        className="w-full h-full"
                        aria-label={`Ver imagen de ${item.title}`}
                      >
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-contain rounded-2xl"
                          loading="lazy"
                        />
                      </button>
                    </div>

                    <div
                      className={`absolute top-0 right-0 w-full h-7 ${item.bgColor} text-sm font-bold flex flex-col items-center justify-center tracking-wide shadow-sm rounded-t-[10px]`}
                    >
                      {item.price.map((price, priceIndex) => (
                        <span key={priceIndex} className="block">
                          {price}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${item.bgColor}`}
                      >
                        <Droplet className={`w-6 h-6 ${item.iconColor}`} />
                      </div>
                      <h3 className="font-bold text-foreground">{item.title}</h3>
                    </div>

                    {item.options.map((option, index) => (
                      <p key={index} className="text-muted-foreground">
                        {option}
                      </p>
                    ))}

                    <div className="text-sm space-y-2 flex-1 overflow-y-auto pr-1">
                      {item.description.map((benefit, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-turquoise rounded-full flex-shrink-0" />
                          <span className="text-foreground">{benefit}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-start gap-2">
                      <Sparkle className="w-5 h-5 text-yellow-400 fill-current shrink-0 mt-1" />
                      <p className="text-muted-foreground text-sm leading-snug">
                        {formatSuggested(item.suggested)}
                      </p>
                    </div>
                  </div>
                }
                backContent={
                  <div className="flex flex-col gap-4 h-full p-4">
                    <ul className="list-disc pl-4 text-sm space-y-1 pr-1">
                      <p>{item.backInfo}</p>
                    </ul>
                  </div>
                }
                bgCard={item.bgCard}
                bgButton={item.bgButton}
                detailLabel={labels["footer.see.more"] || "Ver mas"}
                detailAriaLabel={`${labels["footer.see.more"] || "Ver mas"}: ${item.title}`}
              />
            ))}
          </div>
        </div>
      </section>

      {selectedImage
        ? createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={() => setSelectedImage(null)}
            role="dialog"
            aria-modal="true"
            aria-label={selectedImage.alt}
          >
            <div
              className="relative max-h-[90vh] max-w-[90vw]"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setSelectedImage(null)}
                className="absolute -top-3 -right-3 rounded-full bg-white/90 p-2 text-slate-700 shadow-md"
                aria-label="Cerrar imagen"
              >
                <X className="h-5 w-5" />
              </button>

              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="max-h-[90vh] max-w-[90vw] rounded-2xl bg-white object-contain"
              />
            </div>
          </div>,
          document.body
        )
        : null}
    </>
  );
}
